import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../services/firebase';
import { collection, addDoc, getDocs, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const AdminTreeHealth = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('MY_TREES');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // Trees Management
  const [trees, setTrees] = useState([]);
  const [loadingTrees, setLoadingTrees] = useState(true);
  const [showAddTreeModal, setShowAddTreeModal] = useState(false);
  const [selectedTree, setSelectedTree] = useState(null);
  const [showTreeDetailsModal, setShowTreeDetailsModal] = useState(false);
  
  // Add Tree Form
  const [newTree, setNewTree] = useState({
    name: '',
    species: '',
    location: '',
    plantedDate: '',
    height: '',
    diameter: '',
    condition: 'healthy',
    notes: '',
    tags: []
  });
  
  // AI Health Analysis
  const [showHealthAnalysis, setShowHealthAnalysis] = useState(false);
  const [analyzingHealth, setAnalyzingHealth] = useState(false);
  const [healthAnalysisResult, setHealthAnalysisResult] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const fileInputRef = useRef(null);
  
  // Tree Types Management
  const [treeTypes, setTreeTypes] = useState([]);
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [newTreeType, setNewTreeType] = useState({
    name: '',
    scientificName: '',
    category: '',
    characteristics: '',
    careInstructions: '',
    commonIssues: ''
  });

  useEffect(() => {
    if (currentUser) {
      loadTrees();
      loadTreeTypes();
    }
  }, [currentUser]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadTrees = async () => {
    setLoadingTrees(true);
    try {
      const treesRef = collection(db, 'trees');
      const q = query(treesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const treesList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        treesList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          plantedDate: data.plantedDate || 'Unknown'
        });
      });
      
      setTrees(treesList);
    } catch (error) {
      console.error('Error loading trees:', error);
      showNotification('Failed to load trees. Please try again.', 'error');
    } finally {
      setLoadingTrees(false);
    }
  };

  const loadTreeTypes = async () => {
    try {
      const typesRef = collection(db, 'treeTypes');
      const querySnapshot = await getDocs(typesRef);
      
      const typesList = [];
      querySnapshot.forEach((doc) => {
        typesList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setTreeTypes(typesList);
    } catch (error) {
      console.error('Error loading tree types:', error);
    }
  };

  const handleAddTree = async () => {
    if (!newTree.name.trim() || !newTree.species.trim()) {
      showNotification('Please fill in tree name and species.', 'error');
      return;
    }

    setLoading(true);
    try {
      const treeData = {
        ...newTree,
        adminId: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        healthHistory: [],
        lastInspection: null
      };

      const docRef = await addDoc(collection(db, 'trees'), treeData);
      
      const newTreeWithId = {
        id: docRef.id,
        ...treeData
      };
      
      setTrees([newTreeWithId, ...trees]);
      setShowAddTreeModal(false);
      setNewTree({
        name: '',
        species: '',
        location: '',
        plantedDate: '',
        height: '',
        diameter: '',
        condition: 'healthy',
        notes: '',
        tags: []
      });
      
      showNotification('Tree added successfully!', 'success');
    } catch (error) {
      console.error('Error adding tree:', error);
      showNotification('Failed to add tree. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTreeType = async () => {
    if (!newTreeType.name.trim() || !newTreeType.scientificName.trim()) {
      showNotification('Please fill in tree type name and scientific name.', 'error');
      return;
    }

    setLoading(true);
    try {
      const typeData = {
        ...newTreeType,
        createdAt: new Date(),
        createdBy: currentUser.uid
      };

      const docRef = await addDoc(collection(db, 'treeTypes'), typeData);
      
      const newTypeWithId = {
        id: docRef.id,
        ...typeData
      };
      
      setTreeTypes([...treeTypes, newTypeWithId]);
      setShowAddTypeModal(false);
      setNewTreeType({
        name: '',
        scientificName: '',
        category: '',
        characteristics: '',
        careInstructions: '',
        commonIssues: ''
      });
      
      showNotification('Tree type added successfully!', 'success');
    } catch (error) {
      console.error('Error adding tree type:', error);
      showNotification('Failed to add tree type. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const analyzeTreeHealth = async () => {
    if (!uploadedImage) {
      showNotification('Please upload an image first.', 'error');
      return;
    }

    // Check if OpenAI API key is available
    if (!process.env.REACT_APP_OPENAI_API_KEY) {
      showNotification('OpenAI API key not configured. Please check your environment variables.', 'error');
      return;
    }

    setAnalyzingHealth(true);
    try {
      console.log('Starting tree health analysis...');
      
      // Upload image to Firebase Storage
      console.log('Uploading image to Firebase Storage...');
      const imageRef = ref(storage, `tree-analysis/${Date.now()}_${uploadedImage.name}`);
      await uploadBytes(imageRef, uploadedImage);
      const imageUrl = await getDownloadURL(imageRef);
      console.log('Image uploaded successfully:', imageUrl);

      // Analyze with OpenAI Vision API
      console.log('Sending request to OpenAI API...');
      const requestBody = {
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: `You are a professional arborist and tree health expert. Analyze the uploaded tree image and provide a comprehensive health assessment. 

            Please provide your analysis in the following JSON format:
            {
              "overallHealth": "excellent|good|fair|poor|critical",
              "healthScore": 85,
              "issues": ["list of identified issues"],
              "recommendations": ["list of care recommendations"],
              "species": "identified tree species if possible",
              "urgentConcerns": ["immediate issues requiring attention"],
              "seasonalCare": ["seasonal care suggestions"],
              "estimatedAge": "approximate age range",
              "confidence": "high|medium|low"
            }

            Focus on:
            - Leaf/needle condition and color
            - Branch structure and bark health
            - Signs of disease, pests, or damage
            - Overall tree structure and stability
            - Environmental stress indicators
            - Growth patterns and vigor`
          },
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: 'Please analyze this tree image and provide a comprehensive health assessment in JSON format.'
              },
              {
                type: 'image_url',
                image_url: {
                  url: imageUrl,
                  detail: 'high'
                }
              }
            ]
          }
        ],
        max_tokens: 1500,
        temperature: 0.3
      };

      console.log('Request body prepared:', JSON.stringify(requestBody, null, 2));

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('OpenAI API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('OpenAI API error response:', errorText);
        throw new Error(`OpenAI API request failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('OpenAI API response data:', data);
      
      const analysisText = data.choices[0]?.message?.content?.trim();
      console.log('Analysis text received:', analysisText);
      
      if (analysisText) {
        try {
          // Try to parse as JSON first
          const analysisJson = JSON.parse(analysisText);
          console.log('Successfully parsed JSON analysis:', analysisJson);
          
          setHealthAnalysisResult({
            ...analysisJson,
            imageUrl: imageUrl,
            analyzedAt: new Date(),
            rawResponse: analysisText
          });
        } catch (parseError) {
          console.warn('Failed to parse JSON, using text response:', parseError);
          // If JSON parsing fails, use text response
          setHealthAnalysisResult({
            overallHealth: 'good',
            healthScore: 75,
            issues: ['Analysis provided in text format'],
            recommendations: ['Review the detailed analysis below'],
            rawResponse: analysisText,
            imageUrl: imageUrl,
            analyzedAt: new Date(),
            confidence: 'medium',
            species: 'Unable to determine from analysis'
          });
        }
        
        showNotification('Tree health analysis completed successfully!', 'success');
      } else {
        throw new Error('No analysis content received from OpenAI API');
      }
    } catch (error) {
      console.error('Detailed error analyzing tree health:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to analyze tree health. ';
      if (error.message.includes('401')) {
        errorMessage += 'Invalid API key. Please check your OpenAI API key configuration.';
      } else if (error.message.includes('429')) {
        errorMessage += 'Rate limit exceeded. Please try again in a few minutes.';
      } else if (error.message.includes('400')) {
        errorMessage += 'Invalid request. Please try with a different image.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage += 'Network error. Please check your internet connection.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setAnalyzingHealth(false);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('Please select a valid image file.', 'error');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('Image size must be less than 10MB.', 'error');
      return;
    }

    setUploadedImage(file);
  };

  const saveHealthAnalysis = async (treeId) => {
    if (!healthAnalysisResult || !treeId) return;

    try {
      const treeRef = doc(db, 'trees', treeId);
      const healthRecord = {
        ...healthAnalysisResult,
        recordedAt: new Date(),
        recordedBy: currentUser.uid
      };

      // Update tree with new health record
      await updateDoc(treeRef, {
        lastInspection: new Date(),
        condition: healthAnalysisResult.overallHealth,
        healthHistory: [...(selectedTree.healthHistory || []), healthRecord],
        updatedAt: new Date()
      });

      // Update local state
      setTrees(trees.map(tree => 
        tree.id === treeId 
          ? {
              ...tree,
              lastInspection: new Date(),
              condition: healthAnalysisResult.overallHealth,
              healthHistory: [...(tree.healthHistory || []), healthRecord]
            }
          : tree
      ));

      showNotification('Health analysis saved to tree record!', 'success');
      setShowHealthAnalysis(false);
      setHealthAnalysisResult(null);
      setUploadedImage(null);
    } catch (error) {
      console.error('Error saving health analysis:', error);
      showNotification('Failed to save health analysis.', 'error');
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'excellent': return '#10b981';
      case 'good': return '#22c55e';
      case 'fair': return '#eab308';
      case 'poor': return '#f97316';
      case 'critical': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getConditionIcon = (condition) => {
    switch (condition) {
      case 'excellent': return '🌟';
      case 'good': return '✅';
      case 'fair': return '⚠️';
      case 'poor': return '🔶';
      case 'critical': return '🚨';
      default: return '❓';
    }
  };

  return (
    <Layout>
      <div className="animate-fade-in-up">
        {/* Modern Toast Notification */}
        {notification && (
          <div className={`notification notification-${notification.type}`}>
            {notification.message}
          </div>
        )}

        {/* Modern Header */}
        <div style={{ marginBottom: 'var(--space-12)' }}>
          <h1 className="heading-1" style={{ marginBottom: 'var(--space-2)' }}>
            🌳 My Trees & <span className="text-gradient">Health Management</span>
          </h1>
          <p className="text-muted" style={{ fontSize: '1.125rem', margin: 0 }}>
            Manage your tree inventory and monitor health with AI-powered analysis
          </p>
        </div>

        {/* Modern Tabs */}
        <div className="modern-card" style={{ marginBottom: 'var(--space-8)' }}>
          <div className="tabs">
            {[
              { key: 'MY_TREES', label: 'My Trees', icon: '🌳' },
              { key: 'TREE_TYPES', label: 'Tree Types', icon: '📚' },
              { key: 'HEALTH_ANALYSIS', label: 'AI Health Analysis', icon: '🔬' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`tab ${activeTab === tab.key ? 'active' : ''}`}
              >
                <span style={{ fontSize: '1.2rem' }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Modern Tab Content */}
          <div className="card-body-modern">
            {/* MY TREES Tab */}
            {activeTab === 'MY_TREES' && (
              <div className="animate-fade-in-scale">
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: 'var(--space-8)' 
                }}>
                  <div>
                    <h2 className="heading-2" style={{ margin: 0, fontSize: '1.5rem' }}>
                      Tree Inventory
                    </h2>
                    <p className="text-muted" style={{ margin: 0, fontSize: '0.875rem' }}>
                      Manage and track your tree collection
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddTreeModal(true)}
                    className="btn btn-primary"
                    style={{ animationDelay: '0.2s', animation: 'fadeInScale 0.5s ease-out both' }}
                  >
                    <span style={{ fontSize: '1.2rem' }}>🌱</span>
                    Add New Tree
                  </button>
                </div>

                {loadingTrees ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: 'var(--space-16)', 
                    color: 'var(--gray-500)' 
                  }}>
                    <div className="loading-spinner" style={{ 
                      margin: '0 auto var(--space-4)', 
                      width: '40px', 
                      height: '40px' 
                    }}></div>
                    <p>Loading your tree collection...</p>
                  </div>
                ) : trees.length > 0 ? (
                  <div className="grid grid-3" style={{ gap: 'var(--space-6)' }}>
                    {trees.map((tree, index) => (
                      <div
                        key={tree.id}
                        className="modern-card"
                        style={{
                          cursor: 'pointer',
                          animationDelay: `${0.3 + index * 0.1}s`,
                          animation: 'fadeInScale 0.5s ease-out both'
                        }}
                        onClick={() => {
                          setSelectedTree(tree);
                          setShowTreeDetailsModal(true);
                        }}
                      >
                        <div className="card-header-modern">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <h3 className="heading-3" style={{ margin: '0 0 var(--space-1) 0', fontSize: '1.25rem' }}>
                                {tree.name}
                              </h3>
                              <p className="text-muted" style={{ margin: 0, fontSize: '0.875rem', fontStyle: 'italic' }}>
                                {tree.species}
                              </p>
                            </div>
                            
                            <div className={`status-badge-modern ${
                              tree.condition === 'excellent' || tree.condition === 'good' ? 'status-success-modern' :
                              tree.condition === 'fair' ? 'status-warning-modern' :
                              'status-error-modern'
                            }`}>
                              {getConditionIcon(tree.condition)}
                              {tree.condition}
                            </div>
                          </div>
                        </div>
                        
                        <div className="card-body-modern">
                          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-4)' }}>
                            {tree.location && (
                              <div style={{ marginBottom: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <span>📍</span>
                                <span><strong>Location:</strong> {tree.location}</span>
                              </div>
                            )}
                            {tree.height && (
                              <div style={{ marginBottom: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <span>📏</span>
                                <span><strong>Height:</strong> {tree.height}</span>
                              </div>
                            )}
                            {tree.plantedDate && (
                              <div style={{ marginBottom: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <span>📅</span>
                                <span><strong>Planted:</strong> {tree.plantedDate}</span>
                              </div>
                            )}
                          </div>

                          {tree.lastInspection && (
                            <div style={{
                              background: 'rgba(59, 130, 246, 0.1)',
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                              borderRadius: 'var(--radius-lg)',
                              padding: 'var(--space-3)',
                              fontSize: '0.875rem',
                              color: 'var(--info)',
                              marginBottom: 'var(--space-4)'
                            }}>
                              🔍 Last inspection: {new Date(tree.lastInspection.toDate ? tree.lastInspection.toDate() : tree.lastInspection).toLocaleDateString()}
                            </div>
                          )}

                          {tree.notes && (
                            <div style={{
                              padding: 'var(--space-3)',
                              background: 'var(--gray-50)',
                              borderRadius: 'var(--radius-lg)',
                              fontSize: '0.875rem',
                              color: 'var(--gray-600)',
                              borderLeft: '3px solid var(--primary-500)'
                            }}>
                              {tree.notes.length > 100 ? tree.notes.substring(0, 100) + '...' : tree.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    color: 'var(--gray-500)', 
                    padding: 'var(--space-16)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      fontSize: '6rem', 
                      marginBottom: 'var(--space-6)', 
                      opacity: 0.3,
                      animation: 'pulse 2s infinite'
                    }}>
                      🌳
                    </div>
                    <h3 style={{ 
                      margin: '0 0 var(--space-3) 0', 
                      color: 'var(--gray-700)', 
                      fontSize: '1.5rem', 
                      fontWeight: '600' 
                    }}>
                      No trees in inventory yet
                    </h3>
                    <p style={{ 
                      color: 'var(--gray-500)', 
                      fontSize: '1.125rem', 
                      marginBottom: 'var(--space-8)',
                      maxWidth: '400px'
                    }}>
                      Start building your tree inventory by adding your first tree and tracking its health over time.
                    </p>
                    <button 
                      onClick={() => setShowAddTreeModal(true)}
                      className="btn btn-primary btn-lg"
                      style={{ animation: 'fadeInScale 0.5s ease-out 0.3s both' }}
                    >
                      🌱 Add Your First Tree
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* TREE TYPES Tab */}
            {activeTab === 'TREE_TYPES' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                    Tree Types Database
                  </h2>
                  <button
                    onClick={() => setShowAddTypeModal(true)}
                    style={{
                      background: '#2e7d32',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ fontSize: '1.2rem' }}>📚</span>
                    Add Tree Type
                  </button>
                </div>

                {treeTypes.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '1.5rem' }}>
                    {treeTypes.map((type) => (
                      <div
                        key={type.id}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '1.5rem',
                          backgroundColor: '#ffffff',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
                          {type.name}
                        </h3>
                        <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
                          {type.scientificName}
                        </p>
                        
                        {type.category && (
                          <div style={{
                            background: '#f3f4f6',
                            color: '#374151',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            display: 'inline-block',
                            marginBottom: '1rem'
                          }}>
                            {type.category}
                          </div>
                        )}

                        {type.characteristics && (
                          <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                              Characteristics:
                            </h4>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280', lineHeight: '1.4' }}>
                              {type.characteristics}
                            </p>
                          </div>
                        )}

                        {type.careInstructions && (
                          <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                              Care Instructions:
                            </h4>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280', lineHeight: '1.4' }}>
                              {type.careInstructions}
                            </p>
                          </div>
                        )}

                        {type.commonIssues && (
                          <div>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                              Common Issues:
                            </h4>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280', lineHeight: '1.4' }}>
                              {type.commonIssues}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#666', padding: '3rem' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>📚</div>
                    <h3 style={{ marginBottom: '0.5rem', color: '#374151', fontSize: '1.25rem', fontWeight: '600' }}>
                      No tree types defined yet
                    </h3>
                    <p style={{ color: '#6b7280', fontSize: '1rem', marginBottom: '1.5rem' }}>
                      Build your tree knowledge database by adding tree types and their characteristics.
                    </p>
                    <button 
                      onClick={() => setShowAddTypeModal(true)}
                      style={{
                        background: '#2e7d32',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.75rem 1.5rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      📚 Add First Tree Type
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Add Tree Modal */}
        {showAddTreeModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                  🌱 Add New Tree
                </h2>
                <button 
                  onClick={() => setShowAddTreeModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Tree Name *
                  </label>
                  <input
                    type="text"
                    value={newTree.name}
                    onChange={(e) => setNewTree({...newTree, name: e.target.value})}
                    placeholder="e.g., Front Yard Oak"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Species *
                  </label>
                  <input
                    type="text"
                    value={newTree.species}
                    onChange={(e) => setNewTree({...newTree, species: e.target.value})}
                    placeholder="e.g., White Oak (Quercus alba)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Location
                </label>
                <input
                  type="text"
                  value={newTree.location}
                  onChange={(e) => setNewTree({...newTree, location: e.target.value})}
                  placeholder="e.g., Front yard, near driveway"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Planted Date
                  </label>
                  <input
                    type="date"
                    value={newTree.plantedDate}
                    onChange={(e) => setNewTree({...newTree, plantedDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Height
                  </label>
                  <input
                    type="text"
                    value={newTree.height}
                    onChange={(e) => setNewTree({...newTree, height: e.target.value})}
                    placeholder="e.g., 25 ft"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Diameter
                  </label>
                  <input
                    type="text"
                    value={newTree.diameter}
                    onChange={(e) => setNewTree({...newTree, diameter: e.target.value})}
                    placeholder="e.g., 18 in"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Current Condition
                </label>
                <select
                  value={newTree.condition}
                  onChange={(e) => setNewTree({...newTree, condition: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Notes
                </label>
                <textarea
                  value={newTree.notes}
                  onChange={(e) => setNewTree({...newTree, notes: e.target.value})}
                  rows="3"
                  placeholder="Additional notes about this tree..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowAddTreeModal(false)}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddTree}
                  disabled={loading}
                  style={{
                    background: loading ? '#9ca3af' : '#2e7d32',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  {loading ? 'Adding...' : 'Add Tree'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Tree Type Modal */}
        {showAddTypeModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                  📚 Add Tree Type
                </h2>
                <button 
                  onClick={() => setShowAddTypeModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Common Name *
                  </label>
                  <input
                    type="text"
                    value={newTreeType.name}
                    onChange={(e) => setNewTreeType({...newTreeType, name: e.target.value})}
                    placeholder="e.g., White Oak"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Scientific Name *
                  </label>
                  <input
                    type="text"
                    value={newTreeType.scientificName}
                    onChange={(e) => setNewTreeType({...newTreeType, scientificName: e.target.value})}
                    placeholder="e.g., Quercus alba"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Category
                </label>
                <input
                  type="text"
                  value={newTreeType.category}
                  onChange={(e) => setNewTreeType({...newTreeType, category: e.target.value})}
                  placeholder="e.g., Deciduous, Evergreen, Fruit Tree"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Characteristics
                </label>
                <textarea
                  value={newTreeType.characteristics}
                  onChange={(e) => setNewTreeType({...newTreeType, characteristics: e.target.value})}
                  rows="3"
                  placeholder="Describe the tree's appearance, size, leaf shape, bark, etc."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Care Instructions
                </label>
                <textarea
                  value={newTreeType.careInstructions}
                  onChange={(e) => setNewTreeType({...newTreeType, careInstructions: e.target.value})}
                  rows="3"
                  placeholder="Watering, pruning, fertilizing, and other care requirements"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Common Issues
                </label>
                <textarea
                  value={newTreeType.commonIssues}
                  onChange={(e) => setNewTreeType({...newTreeType, commonIssues: e.target.value})}
                  rows="3"
                  placeholder="Common diseases, pests, and problems to watch for"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowAddTypeModal(false)}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddTreeType}
                  disabled={loading}
                  style={{
                    background: loading ? '#9ca3af' : '#2e7d32',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  {loading ? 'Adding...' : 'Add Tree Type'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tree Details Modal */}
        {showTreeDetailsModal && selectedTree && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                  🌳 {selectedTree.name}
                </h2>
                <button 
                  onClick={() => setShowTreeDetailsModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                {/* Tree Information */}
                <div>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#374151' }}>
                    Tree Information
                  </h3>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Species</div>
                    <div style={{ fontSize: '1rem', color: '#1f2937', fontWeight: '500' }}>{selectedTree.species}</div>
                  </div>

                  {selectedTree.location && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Location</div>
                      <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedTree.location}</div>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    {selectedTree.height && (
                      <div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Height</div>
                        <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedTree.height}</div>
                      </div>
                    )}
                    {selectedTree.diameter && (
                      <div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Diameter</div>
                        <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedTree.diameter}</div>
                      </div>
                    )}
                  </div>

                  {selectedTree.plantedDate && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Planted Date</div>
                      <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedTree.plantedDate}</div>
                    </div>
                  )}

                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Current Condition</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem' }}>{getConditionIcon(selectedTree.condition)}</span>
                      <span style={{
                        fontSize: '1rem',
                        color: getConditionColor(selectedTree.condition),
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {selectedTree.condition}
                      </span>
                    </div>
                  </div>

                  {selectedTree.notes && (
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Notes</div>
                      <div style={{
                        fontSize: '0.875rem',
                        color: '#1f2937',
                        background: '#f9fafb',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}>
                        {selectedTree.notes}
                      </div>
                    </div>
                  )}
                </div>

                {/* Health History */}
                <div>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#374151' }}>
                    Health History
                  </h3>
                  
                  {selectedTree.lastInspection && (
                    <div style={{
                      background: '#f0f9ff',
                      border: '1px solid #bae6fd',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ fontSize: '0.875rem', color: '#0369a1', fontWeight: '600' }}>
                        Last Inspection: {new Date(selectedTree.lastInspection.toDate ? selectedTree.lastInspection.toDate() : selectedTree.lastInspection).toLocaleDateString()}
                      </div>
                    </div>
                  )}

                  {selectedTree.healthHistory && selectedTree.healthHistory.length > 0 ? (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {selectedTree.healthHistory.map((record, index) => (
                        <div
                          key={index}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '1rem',
                            marginBottom: '1rem',
                            background: '#ffffff'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '1rem' }}>{getConditionIcon(record.overallHealth)}</span>
                              <span style={{
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: getConditionColor(record.overallHealth),
                                textTransform: 'capitalize'
                              }}>
                                {record.overallHealth}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {new Date(record.recordedAt.toDate ? record.recordedAt.toDate() : record.recordedAt).toLocaleDateString()}
                            </div>
                          </div>

                          {record.healthScore > 0 && (
                            <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                              Health Score: {record.healthScore}/100
                            </div>
                          )}

                          {record.species && (
                            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              Species: {record.species}
                            </div>
                          )}

                          {record.issues && record.issues.length > 0 && (
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              Issues: {record.issues.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.5 }}>📊</div>
                      <p style={{ fontSize: '0.875rem' }}>No health records yet</p>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      setShowTreeDetailsModal(false);
                      setActiveTab('HEALTH_ANALYSIS');
                    }}
                    style={{
                      background: '#2e7d32',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🔬 Analyze Health with AI
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminTreeHealth;